import type { ReactNode, ErrorInfo } from 'react';
import { Component } from 'react';

import { useAppStore } from '@/stores/useAppStore';
import { logger } from '@/utils/logger';
import { isDevelopment } from '@shared/utils/env';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Application-level error boundary with store integration and retry support.
 *
 * Catches rendering errors in its subtree, logs them, and updates the global
 * app store with the error state. Renders a styled error card with "Try Again"
 * and "Refresh Page" buttons. In development mode, also displays the error
 * message for debugging. Accepts an optional `fallback` prop for custom error UI.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  /**
   * Initializes the error boundary with a clean state.
   *
   * Sets hasError to false and error to null so the component renders
   * its children normally until an error is caught.
   *
   * @param props - Component properties including children and an optional fallback.
   */
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  /**
   * React lifecycle method that updates state when a descendant throws an error.
   *
   * Captures the error object and sets hasError to true so the next render
   * displays the fallback UI instead of the broken component tree.
   *
   * @param error - The error thrown by a descendant component.
   * @returns Updated state with hasError true and the captured error.
   */
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  /**
   * React lifecycle method invoked after an error is caught during rendering.
   *
   * Logs the error via the application logger and attempts to update the
   * global app store with the error message for cross-component awareness.
   * Silently handles store update failures to avoid cascading errors.
   *
   * @param error - The error thrown by a descendant component.
   * @param errorInfo - An object containing the component stack trace.
   */
  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('ErrorBoundary caught an error:', error, errorInfo);

    // Update store with error information
    try {
      const store = useAppStore.getState();
      if (store && store.setError) {
        store.setError(`Application error: ${error.message}`);
      }
    } catch (storeError) {
      logger.warn('Could not update store with error information:', storeError);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    // Clear error from store
    try {
      const store = useAppStore.getState();
      if (store && store.setError) {
        store.setError(null);
      }
    } catch (storeError) {
      logger.warn('Could not clear error from store:', storeError);
    }
  };

  /**
   * Renders the fallback error UI or the children depending on error state.
   *
   * When an error has been caught, renders the custom `fallback` prop if provided,
   * otherwise renders a styled error card with "Try Again" and "Refresh Page"
   * buttons. In development mode, displays the error message for debugging.
   * When no error exists, renders the children normally.
   *
   * @returns The fallback error UI or the children component tree.
   */
  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="bg-muted flex min-h-screen items-center justify-center">
          <div className="bg-card w-full max-w-md rounded-sm p-6 shadow-lg">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive-subtle">
              <svg
                className="h-6 w-6 text-destructive-text"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>

            <div className="text-center">
              <h1 className="text-foreground mb-2 text-xl font-semibold">Something went wrong</h1>

              <p className="text-muted-foreground mb-6">
                We&apos;re sorry, but something unexpected happened. Please try refreshing the page
                or contact support if the problem persists.
              </p>

              {isDevelopment() && this.state.error && (
                <div className="bg-secondary mb-4 rounded-md p-3 text-left">
                  <p className="text-foreground mb-1 text-sm font-medium">Error Details:</p>
                  <p className="text-muted-foreground font-mono text-xs">
                    {this.state.error.message}
                  </p>
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={this.handleRetry}
                  className="bg-muted-foreground hover:bg-muted-foreground/80 flex-1 rounded-md px-4 py-2 font-medium text-white transition-colors"
                >
                  Try Again
                </button>

                <button
                  onClick={() => window.location.reload()}
                  className="bg-muted-foreground hover:bg-muted-foreground/80 flex-1 rounded-md px-4 py-2 font-medium text-white transition-colors"
                >
                  Refresh Page
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
